import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import EventEndpoints from '../../endpoints/EventEndpoints'
import { Event as EventModel } from '../../model/Event'
import { useApplicationStore } from '../../stores/useApplicationStore'
import { useSessionStore } from '../../stores/useSessionStore'

interface EventContextType {
  event: EventModel | null
  loading: boolean
  setEvent: (event: EventModel) => void
  refreshEvent: () => Promise<EventModel | null>
  // True when the current session is allowed to edit this event (admin OR
  // SCHEDULE_ADMIN+ for the event's client). Tabs use this to disable inputs
  // and hide action buttons that would otherwise call edit endpoints.
  canEdit: boolean
}

const EventContext = createContext<EventContextType>({
  event: null,
  loading: true,
  setEvent: () => {},
  refreshEvent: async () => null,
  canEdit: false,
})

export const useEventContext = () => useContext(EventContext)

export const EventProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { eventId } = useParams<{ eventId: string }>()
  const [event, setEvent] = useState<EventModel | null>(null)
  const [loading, setLoading] = useState(true)
  const { events } = useApplicationStore()
  const hasAdmin = useSessionStore((s) => s.hasAdmin)
  const hasScheduleAdminFor = useSessionStore((s) => s.hasScheduleAdminFor)

  const refreshEvent = useCallback(async (): Promise<EventModel | null> => {
    if (!eventId) return null
    try {
      const fullEvent = await EventEndpoints.getEvent(parseInt(eventId))
      setEvent(fullEvent)
      return fullEvent
    } catch (error) {
      console.error('Failed to load event:', error)
      return null
    }
  }, [eventId])

  useEffect(() => {
    if (!eventId) return
    setLoading(true)
    // Show cached event immediately if available
    const cached = events.find((e) => e.id === parseInt(eventId))
    if (cached) setEvent(cached as any)
    // Then load full event
    refreshEvent().finally(() => setLoading(false))
  }, [eventId])

  const canEdit = !!(
    hasAdmin() ||
    (event?.client && hasScheduleAdminFor(event.client.id))
  )

  return (
    <EventContext.Provider
      value={{ event, loading, setEvent, refreshEvent, canEdit }}
    >
      {children}
    </EventContext.Provider>
  )
}
